import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useUserConfigurations } from "@via/api/configuration";
import {
  detailOf,
  reasonOf,
  statusOf,
} from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import type {
  AgentWorkspaceJobRequestView,
  AgentWorkspaceListResponseBody,
  AgentWorkspaceMountsResponseBody,
  AgentWorkspaceResponseBody,
  CancelWorkspaceJobResponseBody,
  ChainSkipReason,
  DecideJobRequestResponseBody,
  ReplaceAgentWorkspaceMountsRequestBody,
  ReplaceAgentWorkspaceMountsResponseBody,
  SubmitAgentWorkspaceJobResponseBody,
  WorkspaceAttentionResponseBody,
  WorkspaceJobListResponseBody,
  WorkspaceJobStatusView,
  WorkspaceJobView,
} from "common";

/**
 * The agent workspace routes (backend/src/routes/agentWorkspace.ts and the mounts routes in
 * backend/src/routes/mountAllowlist.ts), agent-7-4. Every route answers 404 while the installation
 * keeps AGENT_WORKSPACE_JOBS_ENABLED off, and for a workspace or request that is not the caller's,
 * so the screens gate on the flag and read a 404 as "not yours or not there".
 *
 * This folder is `agent-workspaces`, not `workspaces`: `pages/workspaces` holds the older workspace
 * assets screens, which are a different thing.
 */
export const AGENT_WORKSPACE_BASE = "/api/v1/agent-workspace";

const JOBS_POLL_MS = 15000;
export const PREVIEW_POLL_MS = 5000;

/**
 * Statuses that never change again. Every other status keeps the job list polling, `refused`
 * included: submitting the same key again retries it (agent-6-6).
 */
export const TERMINAL_JOB_STATUSES: readonly WorkspaceJobStatusView[] = [
  "skipped",
  "rejected",
  "expired",
  "dispatch_refused",
  "completed",
  "failed",
];

export const isTerminalJobStatus = (status: WorkspaceJobStatusView): boolean =>
  TERMINAL_JOB_STATUSES.includes(status);

export const JOB_STATUS_TEXT: Readonly<Record<WorkspaceJobStatusView, string>> =
  {
    waiting: "Waiting for an earlier job",
    skipped: "Skipped",
    awaiting_approval: "Waiting for your approval",
    rejected: "Not run",
    expired: "Expired",
    starting: "Starting",
    refused: "Refused",
    uncertain: "Not confirmed yet",
    dispatch_refused: "Refused by the cluster",
    queued: "Queued",
    running: "Running",
    completed: "Completed",
    failed: "Failed",
    unknown: "Unknown",
  };

/** Why a chain step will never run, in a person's words (workspace job chains, spec D3 and D6). */
export const CHAIN_SKIP_REASON_TEXT: Readonly<Record<ChainSkipReason, string>> =
  {
    predecessor_failed: "The job it waited for failed.",
    predecessor_unknown:
      "The job it waited for ended in a state the cluster could not confirm.",
    predecessor_rejected: "The review of the job it waited for was rejected.",
    predecessor_expired: "The review of the job it waited for expired.",
    ancestor_skipped: "A step earlier in the chain was skipped.",
    ancestor_cancelled: "A job earlier in the chain was cancelled.",
    cancelled: "It was cancelled before it started.",
    dispatch_refused: "The cluster refused it.",
  };

/** A refused chain step ends its chain: the key is spent, so running it again needs a new one. */
export const CHAIN_DISPATCH_REFUSED_TEXT =
  "The chain ended here. Declare this job again with a new submission key to run it.";

/**
 * Whether the job list offers Cancel: only a job the scheduler holds (it has a scheduler job id)
 * and that may still be live. An awaiting request is not cancelled, it is reviewed and not run.
 */
export const isCancellable = (
  job: Pick<WorkspaceJobView, "status" | "schedulerJobId">
): boolean =>
  job.schedulerJobId !== null &&
  (job.status === "queued" ||
    job.status === "running" ||
    job.status === "unknown");

export const NO_COMMAND_TEXT = "No command: a session or an app";

/** A job with no request (a session job, an app launch) carries an empty command. */
export const commandText = (command: string): string =>
  command.trim() === "" ? NO_COMMAND_TEXT : command;

export const NOT_YOURS_TEXT =
  "This workspace was not found, or it is not yours.";

export const workspacesQueryKey = ["agent-workspaces"] as const;
export const workspaceQueryKey = (workspaceId?: string): unknown[] => [
  "agent-workspace",
  workspaceId,
];
export const workspaceMountsQueryKey = (workspaceId?: string): unknown[] => [
  "agent-workspace-mounts",
  workspaceId,
];
export const workspaceJobsQueryKey = (workspaceId?: string): unknown[] => [
  "agent-workspace-jobs",
  workspaceId,
];
export const jobRequestQueryKey = (
  workspaceId?: string,
  requestId?: string
): unknown[] => ["agent-workspace-request", workspaceId, requestId];

export const useWorkspaces = (
  enabled: boolean
): UseQueryResult<AgentWorkspaceResponseBody[], Error> =>
  useQuery<AgentWorkspaceResponseBody[], Error>({
    queryKey: workspacesQueryKey,
    queryFn: async () => {
      const res = await axios.get(AGENT_WORKSPACE_BASE);
      return (res.data as AgentWorkspaceListResponseBody).workspaces;
    },
    enabled,
    retry: false,
  });

export const workspaceAttentionQueryKey = [
  "agent-workspace-attention",
] as const;

const ATTENTION_POLL_MS = 60_000;

/**
 * The header badge's count (workspace job chains, spec D8): chain steps of the caller's workspaces
 * that need them. Off while the installation keeps chains off, and polled once a minute.
 */
export const useWorkspaceAttention = (): UseQueryResult<
  WorkspaceAttentionResponseBody,
  Error
> => {
  const { data: configurations } = useUserConfigurations();
  const enabled = Boolean(
    configurations?.AGENT_WORKSPACE_JOBS_ENABLED &&
    configurations?.AGENT_WORKSPACE_JOB_CHAINS_ENABLED
  );
  return useQuery<WorkspaceAttentionResponseBody, Error>({
    queryKey: workspaceAttentionQueryKey,
    queryFn: async () => {
      const res = await axios.get(`${AGENT_WORKSPACE_BASE}/attention`);
      return res.data as WorkspaceAttentionResponseBody;
    },
    enabled,
    retry: false,
    refetchInterval: ATTENTION_POLL_MS,
  });
};

/** Records that the owner opened a workspace, so its skipped steps stop counting. */
export const useMarkWorkspaceSeen = (): UseMutationResult<
  unknown,
  unknown,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workspaceId: string) => {
      const res = await axios.post(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/seen`,
        {}
      );
      return res.data as unknown;
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({ queryKey: workspaceAttentionQueryKey });
    },
  });
};

export const useWorkspace = (
  workspaceId?: string,
  enabled = true
): UseQueryResult<AgentWorkspaceResponseBody, Error> =>
  useQuery<AgentWorkspaceResponseBody, Error>({
    queryKey: workspaceQueryKey(workspaceId),
    queryFn: async () => {
      const res = await axios.get(`${AGENT_WORKSPACE_BASE}/${workspaceId}`);
      return res.data as AgentWorkspaceResponseBody;
    },
    enabled: enabled && Boolean(workspaceId),
    retry: false,
  });

export const useWorkspaceMounts = (
  workspaceId?: string
): UseQueryResult<AgentWorkspaceMountsResponseBody, Error> =>
  useQuery<AgentWorkspaceMountsResponseBody, Error>({
    queryKey: workspaceMountsQueryKey(workspaceId),
    queryFn: async () => {
      const res = await axios.get(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/mounts`
      );
      return res.data as AgentWorkspaceMountsResponseBody;
    },
    enabled: Boolean(workspaceId),
    retry: false,
  });

/** The PUT replaces the whole list, and names the version the list was read at. A 409 means it moved. */
export const useSaveWorkspaceMounts = (
  workspaceId?: string
): UseMutationResult<
  ReplaceAgentWorkspaceMountsResponseBody,
  unknown,
  ReplaceAgentWorkspaceMountsRequestBody
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReplaceAgentWorkspaceMountsRequestBody) => {
      const res = await axios.put(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/mounts`,
        body
      );
      return res.data as ReplaceAgentWorkspaceMountsResponseBody;
    },
    // Only a save that landed reloads the list: a refused one keeps the person's edits on screen.
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: workspaceMountsQueryKey(workspaceId),
      });
    },
  });
};

/** Polls every 15 seconds while any job may still change. */
export const useWorkspaceJobs = (
  workspaceId?: string
): UseQueryResult<WorkspaceJobView[], Error> =>
  useQuery<WorkspaceJobView[], Error>({
    queryKey: workspaceJobsQueryKey(workspaceId),
    queryFn: async () => {
      const res = await axios.get(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/jobs`
      );
      return (res.data as WorkspaceJobListResponseBody).jobs;
    },
    enabled: Boolean(workspaceId),
    retry: false,
    refetchInterval: query =>
      (query.state.data ?? []).some(job => !isTerminalJobStatus(job.status))
        ? JOBS_POLL_MS
        : false,
  });

/** Polls every 5 seconds while the request waits for its preview, so the buttons appear on their own. */
export const useJobRequest = (
  workspaceId?: string,
  requestId?: string,
  enabled = true
): UseQueryResult<AgentWorkspaceJobRequestView, Error> =>
  useQuery<AgentWorkspaceJobRequestView, Error>({
    queryKey: jobRequestQueryKey(workspaceId, requestId),
    queryFn: async () => {
      const res = await axios.get(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/requests/${requestId}`
      );
      return res.data as AgentWorkspaceJobRequestView;
    },
    enabled: enabled && Boolean(workspaceId) && Boolean(requestId),
    retry: false,
    refetchInterval: query => {
      const data = query.state.data;
      return data && data.state === "awaiting_approval" && data.preview === null
        ? PREVIEW_POLL_MS
        : false;
    },
  });

export interface DecideJobRequestInput {
  decision: "approve" | "reject";
  /** The hash of the preview the screen rendered, never one read afterwards. */
  previewHash: string;
}

export const useDecideJobRequest = (
  workspaceId?: string,
  requestId?: string
): UseMutationResult<
  DecideJobRequestResponseBody,
  unknown,
  DecideJobRequestInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ decision, previewHash }: DecideJobRequestInput) => {
      const res = await axios.post(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/requests/${requestId}/${decision}`,
        { previewHash }
      );
      return res.data as DecideJobRequestResponseBody;
    },
    onSettled: (): void => {
      queryClient.invalidateQueries({
        queryKey: jobRequestQueryKey(workspaceId, requestId),
      });
      queryClient.invalidateQueries({
        queryKey: workspaceJobsQueryKey(workspaceId),
      });
    },
  });
};

export const useCancelJob = (
  workspaceId?: string
): UseMutationResult<CancelWorkspaceJobResponseBody, unknown, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (submissionKey: string) => {
      const res = await axios.post(
        `${AGENT_WORKSPACE_BASE}/${workspaceId}/jobs/${encodeURIComponent(submissionKey)}/cancel`
      );
      return res.data as CancelWorkspaceJobResponseBody;
    },
    onSettled: (): void => {
      queryClient.invalidateQueries({
        queryKey: workspaceJobsQueryKey(workspaceId),
      });
    },
  });
};

export const CANCEL_UNREACHABLE_TEXT =
  "The job could not be cancelled right now. It may have just finished. Try again in a minute.";

/** A failed cancel in words. A 503 is a transport that did not answer, or a job that already ended. */
export const cancelErrorText = (error: unknown): string => {
  const status = statusOf(error);
  if (status === 503) return CANCEL_UNREACHABLE_TEXT;
  if (status === 409) {
    return "This job cannot be cancelled now: it has ended, or the cluster has not confirmed it yet.";
  }
  if (status === 404) return NOT_YOURS_TEXT;
  return detailOf(error) ?? "The job could not be cancelled.";
};

export const PREVIEW_CHANGED_TEXT =
  "The job changed since you opened it. Review it again.";
export const REQUEST_EXPIRED_TEXT =
  "This request expired. Ask your AI client to submit the job again.";

/** A failed approve or reject in words. */
export const decideErrorText = (error: unknown): string => {
  const status = statusOf(error);
  if (status === 409) {
    switch (reasonOf(error)) {
      case "preview_changed":
        return PREVIEW_CHANGED_TEXT;
      case "request_expired":
        return REQUEST_EXPIRED_TEXT;
      case "already_decided":
        return "This request was already decided.";
      case "preview_missing":
        return "Foundry Connect is still checking this job's folders. Try again in a moment.";
      default:
        break;
    }
  }
  if (status === 403) {
    return "Approve or reject from your signed in browser session.";
  }
  if (status === 404) {
    return "This job request was not found, or you did not make it.";
  }
  return detailOf(error) ?? "The decision could not be saved.";
};

/** What an approval led to, in words (agent-d68: approving submits the job at once). */
export const outcomeText = (
  outcome: SubmitAgentWorkspaceJobResponseBody
): string => {
  switch (outcome.kind) {
    case "submitted":
      return `The job was submitted to the cluster as job ${outcome.jobId}.`;
    case "awaiting_approval":
      return "The job is still waiting for approval.";
    case "review_stale":
      return "The job changed before it could start. Review it again.";
    case "request_terminal":
      return outcome.state === "expired"
        ? "This request expired before it could start."
        : "This request was not run.";
    case "uncertain":
      return "Foundry Connect could not confirm that the job started. The job list shows it once the cluster answers.";
    case "dispatch_refused":
      return `The cluster refused the job (${outcome.reason}).`;
    case "refused":
      return outcome.message ?? `The job was refused (${outcome.reason}).`;
    case "conflict":
      return "Another submission of this job is in progress.";
    case "configuration_changed":
      return "The run environment's settings changed. Ask your AI client to submit the job again.";
    default:
      return "The job was approved.";
  }
};
